import {
  Badge,
  Button,
  Card,
  CardHeader,
  CellSub,
  EmptyState,
  PageHeader,
  Skeleton,
  Table,
  TableShell,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@provenance/design';
import type { IntegratorSummary } from '@provenance/shared';
import { useQuery } from '@tanstack/react-query';
import { Plug, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { auDate, relative } from '../lib/format';
import { IntegratorStatusBadge } from '../lib/status';

/**
 * The integrators list (spec v3 §20.3).
 *
 * The columns are chosen to answer the only question the platform owner actually opens
 * this screen with: is anything wrong? So traffic, error rate and last-seen are on the
 * row, not behind a click. An integrator that has sent nothing for a week, or whose error
 * rate has moved, should be visible without opening anything.
 */
export function Integrators() {
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['integrators'],
    queryFn: () => api.get<IntegratorSummary[]>('/api/console/platform/integrators'),
    refetchInterval: 60_000,
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Integrators"
        lede="Document-preparation systems that submit certificate requests through the API. SageDocs is the first; onboarding the next one is a job for this screen, not a deploy."
        actions={
          <Button variant="primary" leadingIcon={<Plus className="size-3.5" />}>
            Onboard an integrator
          </Button>
        }
      />

      {query.isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : (query.data ?? []).length === 0 ? (
        <EmptyState
          icon={<Plug className="size-5" />}
          title="No integrators yet"
          description="An integrator is a system that prepares certificates and lodges them for chamber review. Onboard one, grant it access to a chamber, and issue it a key."
          action={
            <Button variant="primary" leadingIcon={<Plus className="size-3.5" />}>
              Onboard an integrator
            </Button>
          }
        />
      ) : (
        <TableShell
          footer={
            <span>
              {query.data!.length} integrator{query.data!.length === 1 ? '' : 's'} · traffic and
              error rate over the last 24 hours
            </span>
          }
        >
          <Table className="min-w-[900px]">
            <Thead>
              <Tr>
                <Th>Integrator</Th>
                <Th>Chambers</Th>
                <Th>Active keys</Th>
                <Th numeric>Requests (24h)</Th>
                <Th numeric>Error rate</Th>
                <Th>Last seen</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {query.data!.map((integrator) => (
                <Tr
                  key={integrator.id}
                  onClick={() => navigate(`/platform/integrators/${integrator.id}`)}
                >
                  <Td>
                    <span className="font-medium">{integrator.name}</span>
                    <CellSub>{integrator.contactEmail}</CellSub>
                  </Td>
                  <Td numeric={false}>
                    <Badge tone={integrator.chamberCount > 0 ? 'leaf' : 'neutral'} size="sm">
                      {integrator.chamberCount}
                    </Badge>
                  </Td>
                  <Td>
                    {integrator.activeKeyCount === 0 ? (
                      <Tooltip content="This integrator cannot call the API until a key is issued">
                        <Badge tone="warn" size="sm">
                          None
                        </Badge>
                      </Tooltip>
                    ) : (
                      <span className="pv-num">{integrator.activeKeyCount}</span>
                    )}
                  </Td>
                  <Td numeric>{integrator.requests24h.toLocaleString()}</Td>
                  <Td numeric>
                    {integrator.requests24h === 0 ? (
                      <span className="text-[var(--pv-faint)]">—</span>
                    ) : (
                      <span
                        className={
                          integrator.errorRate24h > 5
                            ? 'font-semibold text-[var(--pv-stop)]'
                            : integrator.errorRate24h > 1
                              ? 'text-[var(--pv-warn)]'
                              : 'text-[var(--pv-ok)]'
                        }
                      >
                        {integrator.errorRate24h}%
                      </span>
                    )}
                  </Td>
                  <Td>
                    {integrator.lastSeenAt ? (
                      <>
                        {relative(integrator.lastSeenAt)}
                        <CellSub>{auDate(integrator.lastSeenAt)}</CellSub>
                      </>
                    ) : (
                      <span className="text-[var(--pv-faint)]">Never</span>
                    )}
                  </Td>
                  <Td>
                    <IntegratorStatusBadge status={integrator.status} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableShell>
      )}

      <Card>
        <CardHeader
          title="How an integration works"
          description="What an integrator needs from you, in order."
        />
        <ol className="m-0 flex list-none flex-col gap-3 p-0">
          {[
            [
              'Onboard the integrator',
              'Their name and a technical contact. This is the commercial relationship, not a credential.',
            ],
            [
              'Grant them a chamber',
              'Separate from keys on purpose: you can add or revoke a chamber without reissuing credentials, and revoking a leaked key does not cost them their chamber relationships.',
            ],
            [
              'Issue a key',
              'Shown once. Only a hash is stored. Rotate it later with an overlap so nothing breaks.',
            ],
            [
              'Point them at the API reference',
              'The OpenAPI spec and a Postman collection are generated from the running code, so they cannot drift.',
            ],
          ].map(([title, detail], index) => (
            <li key={title} className="grid grid-cols-[auto_minmax(0,1fr)] gap-3">
              <span className="grid size-6 shrink-0 place-items-center rounded-full bg-[var(--pv-brand-soft)] [font-size:11.5px] font-bold text-[var(--pv-ink)]">
                {index + 1}
              </span>
              <span className="min-w-0">
                <span className="block [font-size:13.5px] font-semibold">{title}</span>
                <span className="mt-0.5 block [font-size:13px] leading-relaxed text-[var(--pv-muted)]">
                  {detail}
                </span>
              </span>
            </li>
          ))}
        </ol>
        <p className="mt-4 border-t border-[var(--pv-line)] pt-3 [font-size:var(--pv-text-xs)] text-[var(--pv-faint)]">
          A key's effective permission is the intersection of its integrator's grants and its own
          scope narrowing — a key can only ever be narrower than its integrator, never broader.
        </p>
      </Card>
    </div>
  );
}
